import { nanoid } from "nanoid";
import { splitWaypointsAtFraction } from "./cable-routing";
import { endpointPos } from "./store-helpers";
import { findCable } from "./system-lookup";
import type { Cable, ComponentNode, System } from "./types";

export type SplitCableParams = {
  cableId: string;
  /** Top-left of the new junction node, in flow coords. */
  junctionPos: { x: number; y: number };
  /** Where along the original cable's polyline (0..1) the split occurs. */
  splitFraction: number;
  newConnection: {
    fromNodeId: string;
    fromTerminalId: string;
    lengthM: number;
  };
};

export type CableSplitPlan = {
  junctionId: string;
  newCableId: string;
  /** Two halves of the original cable, plus the freshly attached cable. */
  c1: Cable;
  c2: Cable;
  c3: Cable;
  junction: ComponentNode;
  removedCableId: string;
  newCableIds: [string, string, string];
};

/** Split a cable by inserting a junction; length and any hand-shaped waypoints
 *  divide at the same fraction. */
export function planCableSplit(
  params: SplitCableParams,
  system: System,
): CableSplitPlan | null {
  const cable = findCable(system, params.cableId);
  if (!cable) return null;

  const junctionId = nanoid(8);
  const newCableId = nanoid(8);
  const c1Id = nanoid(8);
  const c2Id = nanoid(8);

  // Clamp so neither half collapses to ~0 m even if the user drops near an end.
  const f = Math.max(0.05, Math.min(0.95, params.splitFraction));
  const c1Length = cable.lengthM * f;
  const c2Length = cable.lengthM * (1 - f);

  const fromPt = endpointPos(cable.fromNodeId, cable.fromTerminalId, system.nodes);
  const toPt = endpointPos(cable.toNodeId, cable.toTerminalId, system.nodes);
  const split =
    cable.waypoints && cable.waypoints.length > 0 && fromPt && toPt
      ? splitWaypointsAtFraction(fromPt, toPt, cable.waypoints, f)
      : { before: [], after: [] };

  const junction: ComponentNode = {
    id: junctionId,
    kind: "component",
    definitionId: "junction",
    position: params.junctionPos,
  };

  // Both halves keep the user's overrides; a junction leaves the series network
  // unchanged, so current recomputes on its own.
  const c1: Cable = {
    id: c1Id,
    fromNodeId: cable.fromNodeId,
    fromTerminalId: cable.fromTerminalId,
    toNodeId: junctionId,
    toTerminalId: "j",
    lengthM: c1Length,
    wireSizeMm2Override: cable.wireSizeMm2Override,
    ...(cable.ampacityDerating != null ? { ampacityDerating: cable.ampacityDerating, vdropClass: cable.vdropClass, notes: cable.notes } : {}),
    fuseAmpOverride: cable.fuseAmpOverride,
    ...(cable.fuseTypeOverride != null ? { fuseTypeOverride: cable.fuseTypeOverride } : {}),
    ...(cable.fuseAmpOverride != null && cable.fuseAmpAuto != null ? { fuseAmpAuto: cable.fuseAmpAuto } : {}),
    ...(split.before.length > 0 ? { waypoints: split.before } : {}),
  };
  const c2: Cable = {
    id: c2Id,
    fromNodeId: junctionId,
    fromTerminalId: "j",
    toNodeId: cable.toNodeId,
    toTerminalId: cable.toTerminalId,
    lengthM: c2Length,
    wireSizeMm2Override: cable.wireSizeMm2Override,
    ...(cable.ampacityDerating != null ? { ampacityDerating: cable.ampacityDerating, vdropClass: cable.vdropClass, notes: cable.notes } : {}),
    fuseAmpOverride: cable.fuseAmpOverride,
    ...(cable.fuseTypeOverride != null ? { fuseTypeOverride: cable.fuseTypeOverride } : {}),
    ...(cable.fuseAmpOverride != null && cable.fuseAmpAuto != null ? { fuseAmpAuto: cable.fuseAmpAuto } : {}),
    ...(split.after.length > 0 ? { waypoints: split.after } : {}),
  };
  const c3: Cable = {
    id: newCableId,
    fromNodeId: params.newConnection.fromNodeId,
    fromTerminalId: params.newConnection.fromTerminalId,
    toNodeId: junctionId,
    toTerminalId: "j",
    lengthM: params.newConnection.lengthM,
  };

  return {
    junctionId,
    newCableId,
    c1,
    c2,
    c3,
    junction,
    removedCableId: cable.id,
    newCableIds: [c1Id, c2Id, newCableId],
  };
}
